import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { NewOperationResponse, ListMapObjectsResponse, ListMapArrowsResponse } from '@warplan/shared';
import { api } from './api-client.js';
import { MAP_OBJECTS_QUERY_KEY } from './map-objects-api.js';
import { MAP_ARROWS_QUERY_KEY } from './map-arrows-api.js';
import { OPERATION_PLAN_QUERY_KEY } from './operation-plan-api.js';

/**
 * Admin "New operation" reset: wipes every map object and arrow with
 * mode="operation" and resets the operation plan to defaults. The realtime
 * channel will also push `objects.cleared` + `arrows.cleared` +
 * `operation-plan.updated` events, but we eagerly sync the local cache so
 * the UI updates without a round-trip.
 */
export function useNewOperation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<NewOperationResponse>('/api/admin/new-operation', {}),
    onSuccess: (res) => {
      // Filter out operation-mode objects from the cache
      const currentObjects = qc.getQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY);
      if (currentObjects?.objects) {
        const filtered = currentObjects.objects.filter((obj) => obj.mode !== 'operation');
        qc.setQueryData(MAP_OBJECTS_QUERY_KEY, { objects: filtered });
      }

      // Filter out operation-mode arrows from the cache
      const currentArrows = qc.getQueryData<ListMapArrowsResponse>(MAP_ARROWS_QUERY_KEY);
      if (currentArrows?.arrows) {
        const filtered = currentArrows.arrows.filter((arrow) => arrow.mode !== 'operation');
        qc.setQueryData(MAP_ARROWS_QUERY_KEY, { arrows: filtered });
      }

      // Update operation plan
      qc.setQueryData(OPERATION_PLAN_QUERY_KEY, res.operationPlan);
    },
  });
}
